import { normalize } from 'normalizr'
import { Schemas } from 'redux-modules/schema'
import { updateEntities, deleteEntity } from 'redux-modules/entity'

import api from 'api'

const FETCH_REQUEST = 'realhub/agencyProviderPriceOverride/FETCH_REQUEST'
const FETCH_SUCCESS = 'realhub/agencyProviderPriceOverride/FETCH_SUCCESS'
const FETCH_FAILURE = 'realhub/agencyProviderPriceOverride/FETCH_FAILURE'
const CREATE_REQUEST = 'realhub/agencyProviderPriceOverride/CREATE_REQUEST'
const CREATE_SUCCESS = 'realhub/agencyProviderPriceOverride/CREATE_SUCCESS'
const CREATE_FAILURE = 'realhub/agencyProviderPriceOverride/CREATE_FAILURE'
const UPDATE_REQUEST = 'realhub/agencyProviderPriceOverride/UPDATE_REQUEST'
const UPDATE_SUCCESS = 'realhub/agencyProviderPriceOverride/UPDATE_SUCCESS'
const UPDATE_FAILURE = 'realhub/agencyProviderPriceOverride/UPDATE_FAILURE'
const DELETE_REQUEST = 'realhub/agencyProviderPriceOverride/DELETE_REQUEST'
const DELETE_SUCCESS = 'realhub/agencyProviderPriceOverride/DELETE_SUCCESS'
const DELETE_FAILURE = 'realhub/agencyProviderPriceOverride/DELETE_FAILURE'

// Initial State
const initialState = {
  loaded: false,
  loadedIds: [],
  loadedForEntities: [],
  loading: false,
  creating: false,
  updating: false,
  deleting: false,
  errors: [],
}

// Actions
export function fetchRequest(payload = {}){
  return {
    type: FETCH_REQUEST,
    loadedIds: payload.loadedIds,
    loadedForEntities: payload.loadedForEntities,
  }
}

export function fetchSuccess(){
  return {
    type: FETCH_SUCCESS,
  }
}

export function fetchFailure(errors = []){
  return {
    type: FETCH_FAILURE,
    errors,
  }
}

export function createRequest(){
  return {
    type: CREATE_REQUEST,
  }
}

export function createSuccess(){
  return {
    type: CREATE_SUCCESS,
  }
}

export function createFailure(errors = []){
  return {
    type: CREATE_FAILURE,
    errors,
  }
}

export function updateRequest(){
  return {
    type: UPDATE_REQUEST,
  }
}

export function updateSuccess(){
  return {
    type: UPDATE_SUCCESS,
  }
}

export function updateFailure(errors = []){
  return {
    type: UPDATE_FAILURE,
    errors,
  }
}

export function deleteRequest(){
  return {
    type: DELETE_REQUEST,
  }
}

export function deleteSuccess(){
  return {
    type: DELETE_SUCCESS,
  }
}

export function deleteFailure(errors = []){
  return {
    type: DELETE_FAILURE,
    errors,
  }
}

// Helper Functions
const handleError = errors => errors || ['Something went wrong']

// Action Creators
export function loadAgencyPriceOverrides(options){
  const entityKey = `Agency${options.agency_id}`
  return (dispatch, getState) => {
    const loadedForEntities = [...getState().agencyProviderPriceOverrides.loadedForEntities]
    if (!loadedForEntities.includes(entityKey)){
      loadedForEntities.push(entityKey)
    }

    dispatch(fetchRequest({ loadedForEntities }))

    const { agency_id } = options
    const promise = api(`/admin/agencies/${agency_id}/price_overrides.json`, options)
      .then(response => response.json().then(json => ({ json, response })))
      .then(({ json, response }) => {
        if (!response.ok){
          return Promise.reject(json)
        }

        const normalizedJson = normalize(json, Schemas.AGENCY_PROVIDER_PRICE_OVERRIDE_ARRAY)
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess())

        return json
      })
      .catch((json) => {
        const errors = handleError(json.errors)
        dispatch(fetchFailure(errors))
      })

    return promise
  }
}

export function createAgencyPriceOverride(agencyProviderPriceOverride, options, callback){
  const config = {
    method: 'POST',
    body: JSON.stringify({
      agency_provider_price_override: agencyProviderPriceOverride,
    }),
  }

  return (dispatch) => {
    // Set Loading
    dispatch(createRequest())

    const { agency_id } = agencyProviderPriceOverride
    const promise = api(`/admin/agencies/${agency_id}/price_overrides.json`, options, config)
      .then(response => response.json().then(json => ({ json, response })))
      .then(({ json, response }) => {
        if (!response.ok){
          return Promise.reject(json)
        }

        const normalizedJson = normalize(json, Schemas.AGENCY_PROVIDER_PRICE_OVERRIDE)
        dispatch(updateEntities(normalizedJson))
        dispatch(createSuccess())

        if (callback) callback(json)

        return json
      })
      .catch((json) => {
        const errors = handleError(json.errors)
        dispatch(createFailure(errors))
      })

    return promise
  }
}

export function updateAgencyPriceOverride(agencyProviderPriceOverride, options, callback){
  const config = {
    method: 'PUT',
    body: JSON.stringify({
      agency_provider_price_override: agencyProviderPriceOverride,
    }),
  }

  return (dispatch) => {
    // Set Loading
    dispatch(updateRequest())

    const { agency_id, id } = agencyProviderPriceOverride
    const promise = api(`/admin/agencies/${agency_id}/price_overrides/${id}.json`, options, config)
      .then(response => response.json().then(json => ({ json, response })))
      .then(({ json, response }) => {
        if (!response.ok){
          return Promise.reject(json)
        }

        const normalizedJson = normalize(json, Schemas.AGENCY_PROVIDER_PRICE_OVERRIDE)
        dispatch(updateEntities(normalizedJson))
        dispatch(updateSuccess())

        if (callback) callback(json)

        return json
      })
      .catch((json) => {
        const errors = handleError(json.errors)
        dispatch(updateFailure(errors))
      })

    return promise
  }
}

export function deleteAgencyPriceOverride(agencyProviderPriceOverride, options, callback){
  const config = {
    method: 'DELETE',
  }

  return (dispatch) => {
    // Set Loading
    dispatch(deleteRequest())

    const { agency_id, id } = agencyProviderPriceOverride
    const promise = api(`/admin/agencies/${agency_id}/price_overrides/${id}.json`, options, config)
      .then(response => response.json().then(json => ({ json, response })))
      .then(({ json, response }) => {
        if (!response.ok){
          return Promise.reject(json)
        }

        const normalizedJson = normalize(json, Schemas.AGENCY_PROVIDER_PRICE_OVERRIDE)
        dispatch(deleteEntity(normalizedJson))
        dispatch(deleteSuccess())

        if (callback) callback(json)

        return json
      })
      .catch((json) => {
        const errors = handleError(json.errors)
        dispatch(deleteFailure(errors))
      })

    return promise
  }
}

// Reducer
export default function reducer(state = initialState, action = {}){
  switch (action.type){
    case FETCH_REQUEST:
      return Object.assign({}, state, {
        loading: true,
        loadedIds: action.loadedIds || state.loadedIds,
        loadedForEntities: action.loadedForEntities || state.loadedForEntities,
      })
    case FETCH_SUCCESS:
      return Object.assign({}, state, {
        loading: false,
        loaded: true,
        errors: [],
      })
    case FETCH_FAILURE:
      return Object.assign({}, state, {
        loading: false,
        loaded: false,
        errors: action.errors,
      })
    case CREATE_REQUEST:
      return Object.assign({}, state, {
        creating: true,
      })
    case CREATE_SUCCESS:
      return Object.assign({}, state, {
        creating: false,
        errors: [],
      })
    case CREATE_FAILURE:
      return Object.assign({}, state, {
        creating: false,
        errors: action.errors,
      })
    case UPDATE_REQUEST:
      return Object.assign({}, state, {
        updating: true,
      })
    case UPDATE_SUCCESS:
      return Object.assign({}, state, {
        updating: false,
        errors: [],
      })
    case UPDATE_FAILURE:
      return Object.assign({}, state, {
        updating: false,
        errors: action.errors,
      })
    case DELETE_REQUEST:
      return Object.assign({}, state, {
        deleting: true,
      })
    case DELETE_SUCCESS:
      return Object.assign({}, state, {
        deleting: false,
        errors: [],
      })
    case DELETE_FAILURE:
      return Object.assign({}, state, {
        deleting: false,
        errors: action.errors,
      })
    default:
      return state
  }
}
